import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { DatastoreCollection, DatastoreDocument } from '@freelancer/datastore';
import {
  ExperiencesCollection,
  FreelancerReputation,
  ProfileViewUsersCollection,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { AnchorScroll } from '@freelancer/ui';
import { FreelancerBreakpoints } from '@freelancer/ui/breakpoints';
import { ButtonColor } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkWeight } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import { StickyBehaviour } from '@freelancer/ui/sticky';
import { TextSize } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { RoleApi } from 'api-typings/common/common';
import {
  ContextTypeApi,
  ThreadTypeApi,
} from 'api-typings/messages/messages_types';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from '../user-profile-award-project.types';
import { UserProfileSection } from '../user-profile.component';

@Component({
  selector: 'app-user-profile-header',
  template: `
    <fl-bit
      *flFeature="Feature.VIEW_REVIEW"
      [flSticky]="true"
      [flStickyBehaviour]="StickyBehaviour.APPEAR_AFTER_SCROLL"
    >
      <fl-container
        *ngIf="profileViewUserDoc.valueChanges() | flAsync as user"
        class="ProfileHeader"
      >
        <app-user-detail
          class="ProfileHeader-userDetail"
          [avatarSize]="AvatarSize.MID"
          [flHideMobile]="true"
          [flMarginRight]="Margin.XXXLARGE"
          [flMarginRightTablet]="Margin.LARGE"
          [headingSize]="TextSize.SMALL"
          [headingSizeTablet]="TextSize.MID"
          [isOnline]="isOnline$ | flAsync"
          [navigateToProfile]="false"
          [ratingSize]="IconSize.SMALL"
          [reputation]="userReputation$ | flAsync"
          [showLocation]="false"
          [showTagline]="false"
          [user]="user"
          (profileLinkClicked)="scrollTo(UserProfileSection.SUMMARY)"
        ></app-user-detail>
        <fl-bit class="ProfileHeader-content">
          <fl-tabs>
            <fl-tab-item
              flTrackingLabel="AboutTab"
              i18n-title="User Summary link"
              title="About Me"
              [selected]="userProfileSection === UserProfileSection.SUMMARY"
              (click)="scrollTo(UserProfileSection.SUMMARY)"
            >
            </fl-tab-item>
            <fl-tab-item
              *ngIf="showUsersPortfolio$ | flAsync"
              flTrackingLabel="PortfolioTab"
              i18n-title="User Portfolio link"
              title="Portfolio Items"
              [selected]="userProfileSection === UserProfileSection.PORTFOLIO"
              (click)="scrollTo(UserProfileSection.PORTFOLIO)"
            >
            </fl-tab-item>
            <fl-tab-item
              *ngIf="showUsersReviews$ | flAsync"
              flTrackingLabel="ReviewsTab"
              i18n-title="User Reviews link"
              title="Reviews"
              [selected]="userProfileSection === UserProfileSection.REVIEWS"
              (click)="scrollTo(UserProfileSection.REVIEWS)"
            >
            </fl-tab-item>
            <fl-tab-item
              *ngIf="(showUsersResume$ | flAsync) && (resumeExists$ | flAsync)"
              flTrackingLabel="UserExperienceTab"
              i18n-title="User Experiences link"
              title="Resume"
              [selected]="userProfileSection === UserProfileSection.RESUME"
              (click)="scrollTo(UserProfileSection.RESUME)"
            >
            </fl-tab-item>
          </fl-tabs>

          <fl-bit class="ProfileHeader-buttons" [flHideMobile]="true">
            <fl-chat-button
              *ngIf="isAgentLoggedUser$ | flAsync"
              flTrackingLabel="HeaderChatButton"
              [flMarginRight]="Margin.SMALL"
              [otherMemberIds]="[user.id]"
              [contextType]="ContextTypeApi.SUPPORT_CHAT"
              [threadType]="ThreadTypeApi.ADMIN_PREFERRED_CHAT"
              [contextId]="user.id"
            ></fl-chat-button>
            <app-user-profile-hire-me-action-state
              [awardProjectHireMeState$]="awardProjectHireMeState$"
            >
              <ng-container loading-state>
                <ng-template [ngTemplateOutlet]="loading"></ng-template>
              </ng-container>

              <ng-container award-project-state>
                <ng-container
                  *ngIf="
                    awardProjectBid$ | flAsync as awardProjectModel;
                    else loading
                  "
                >
                  <fl-button
                    i18n="User Profile Award sticky header button"
                    flTrackingLabel="HeaderAwardMeButton"
                    [busy]="awardMeBusy"
                    [color]="ButtonColor.SUCCESS"
                    [flMarginRight]="Margin.SMALL"
                    [fragment]="'proposals'"
                    [link]="awardProjectModel.projectLink"
                    [queryParams]="{ awardBid: awardProjectModel.bidId }"
                    (click)="handleHireMeClicked()"
                  >
                    Award
                  </fl-button>
                  <fl-chat-button
                    *ngIf="!(isAgentLoggedUser$ | flAsync)"
                    flTrackingLabel="HeaderChatButton"
                    [otherMemberIds]="[awardProjectModel.freelancerId]"
                    [contextType]="ContextTypeApi.PROJECT"
                    [contextId]="awardProjectModel.projectId"
                  ></fl-chat-button>
                </ng-container>
              </ng-container>

              <ng-container hire-me-state>
                <fl-button
                  i18n="User Profile Hire Me sticky header button"
                  flTrackingLabel="HeaderHireMeButton"
                  [color]="ButtonColor.SECONDARY"
                  (click)="hireMeClicked.emit()"
                >
                  <fl-icon
                    [size]="IconSize.SMALL"
                    [color]="IconColor.WHITE"
                    [name]="'ui-bird'"
                  ></fl-icon>
                  Hire Me
                </fl-button>
              </ng-container>

              <ng-template #loading>
                <fl-button
                  flTrackingLabel="HeaderLoadingButton"
                  [flMarginBottom]="Margin.MID"
                  [disabled]="true"
                  [busy]="(awardProjectBid$ | flAsync) === undefined"
                ></fl-button>
              </ng-template>
            </app-user-profile-hire-me-action-state>
          </fl-bit>
        </fl-bit>
      </fl-container>
    </fl-bit>
  `,
  styleUrls: ['./user-profile-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileHeaderComponent implements OnInit {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  Feature = Feature;
  FreelancerBreakpoints = FreelancerBreakpoints;
  TextSize = TextSize;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkWeight = LinkWeight;
  Margin = Margin;
  ModalSize = ModalSize;
  StickyBehaviour = StickyBehaviour;
  UserProfileSection = UserProfileSection;
  ContextTypeApi = ContextTypeApi;
  ThreadTypeApi = ThreadTypeApi;

  @Input() awardProjectBid$: Rx.Observable<AwardProjectModel>;
  @Input() awardProjectHireMeState$: Rx.Observable<AwardProjectHireMeState>;
  @Input() experiencesCollection: DatastoreCollection<ExperiencesCollection>;
  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isAgentLoggedUser$: Rx.Observable<boolean>;
  @Input() isOnline$: Rx.Observable<boolean>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  @Input() profileUserRole$: Rx.Observable<RoleApi>;
  @Input() profileViewUserDoc: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() resumeExists$: Rx.Observable<boolean>;
  @Input() showUsersPortfolio$: Rx.Observable<boolean>;
  @Input() showUsersResume$: Rx.Observable<boolean>;
  @Input() showUsersReviews$: Rx.Observable<boolean>;
  @Input() userProfileSection?: UserProfileSection | undefined;
  @Input() userReputation$: Rx.Observable<FreelancerReputation>;

  @Output() hireMeClicked = new EventEmitter<void>();

  link$: Rx.Observable<string>;
  awardMeBusy = false;

  constructor(private anchorScroll: AnchorScroll) {}

  ngOnInit() {
    this.link$ = this.profileViewUserDoc
      .valueChanges()
      .pipe(map(u => `/u/${u.username}`));
  }

  handleHireMeClicked(): void {
    this.awardMeBusy = true;
  }

  scrollTo(name: UserProfileSection) {
    this.anchorScroll.scrollTo(name);
  }
}
